import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronDown, CheckCircle2, ShieldCheck, Star, FileText, IndianRupee, Zap, Send, Layers, Eye } from "lucide-react";
import SEO from "../seo/SEO";
import { ORGANIZATION_SCHEMA, SOFTWARE_SCHEMA, buildBreadcrumbSchema, buildFAQSchema, SITE_DOMAIN } from "../seo/seoConfig";
import { PROFESSIONS } from "../seo/professionsConfig";
import { SEO_LANDING_PAGES } from "../seo/landingPagesConfig";

const heading = { fontFamily: "'Satoshi', sans-serif" };

const COMMON_FAQS = (profession) => [
  { q: `Can ${profession} use this invoice format for GST?`, a: `Yes — InvoiceSetu auto-applies the right format. If you have a GSTIN, it generates a Tax Invoice with CGST/SGST or IGST split. If you don't, it switches to Bill of Supply automatically.` },
  { q: `Is the invoice tool free for ${profession}?`, a: `Yes — the free plan covers unlimited invoices, customer master, Email/WhatsApp share and professional PDF. No credit card required.` },
  { q: `Can I send the invoice on WhatsApp?`, a: `Yes — every invoice generates a share link with a payment link embedded. Send by WhatsApp, Email or SMS — clients pay before you call them.` },
  { q: `Will it handle multiple GST rates?`, a: `Yes — each line item can have its own GST rate (5% / 12% / 18% / 28%). HSN-wise summary auto-generates for clean GSTR-1 filing.` },
];

export default function ProfessionLandingPage() {
  const { profession: slug } = useParams();
  const data = PROFESSIONS.find(p => p.slug === slug);
  if (!data) return <Navigate to="/" replace />;

  const faqs = COMMON_FAQS(data.profession);
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Invoice Format", url: "/" },
    { name: data.profession, url: `/invoice-format/${data.slug}` },
  ];

  return (
    <div className="bg-white min-h-screen" data-testid={`profession-${data.slug}`}>
      <SEO
        title={`Invoice Format for ${data.profession} (India 2026)`}
        description={`Free invoice format for ${data.profession.toLowerCase()} in India — GST-ready, professional PDF, Email/WhatsApp share. ${data.intro.split('.')[0]}.`}
        path={`/invoice-format/${data.slug}`}
        schemas={[
          ORGANIZATION_SCHEMA,
          SOFTWARE_SCHEMA,
          buildBreadcrumbSchema(breadcrumbs),
          buildFAQSchema(faqs),
        ]}
      />

      {/* Header */}
      <div className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4" /> InvoiceSetu
          </Link>
          <Link to="/auth" className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 text-xs sm:text-sm" data-testid="prof-cta-header">Start Free</Link>
        </div>
      </div>

      {/* Hero */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600 bg-blue-50 rounded-full px-3 py-1 mb-5"
          >
            For {data.profession}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="text-3xl sm:text-5xl font-black text-slate-900 leading-[1.1] mb-4"
            style={heading}
          >
            Invoice Format for {data.profession}
          </motion.h1>
          <p className="text-base sm:text-lg text-slate-600 mb-3 max-w-2xl mx-auto">{data.heroLine}</p>
          <p className="text-sm text-slate-500 mb-7 max-w-2xl mx-auto">{data.intro}</p>
          <div className="flex flex-wrap justify-center gap-3 mb-5">
            <Link
              to="/auth"
              data-testid="prof-cta-primary"
              className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-6 py-3 hover:bg-blue-700 transition-colors"
            >
              Create Free Invoice <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/sample/invoice"
              className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-900 font-semibold rounded-lg px-6 py-3 hover:border-slate-300 hover:bg-slate-50 transition-colors"
            >
              View Sample Invoice
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Free plan available</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No credit card</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> India-first design</span>
          </div>
        </div>
      </section>

      {/* What's specific to this profession */}
      <section className="px-4 sm:px-6 py-12 sm:py-14 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-8" style={heading}>
            What's specific to {data.profession}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.specifics.map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample line items table */}
      <section className="px-4 sm:px-6 py-12 sm:py-14">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-2" style={heading}>
            Typical line items for {data.profession}
          </h2>
          <p className="text-sm text-slate-500 text-center mb-8">A starting point — customise rates & GST percent in seconds.</p>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Item</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">HSN/SAC</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Qty</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Rate</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">GST</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.sampleItems.map((it, i) => {
                  const total = it.qty * it.rate * (1 + (it.gst || 0) / 100);
                  return (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-slate-900">{it.name}</td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">{it.hsn}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.qty}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.rate < 0 ? `-₹${Math.abs(it.rate)}` : `₹${it.rate}`}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.gst}%</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{total < 0 ? `-₹${Math.abs(total).toLocaleString("en-IN")}` : `₹${total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Common features bar */}
      <section className="px-4 sm:px-6 py-12 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 text-center mb-8" style={heading}>
            Everything {data.profession} need, included
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { i: Zap, t: "30-second invoices" },
              { i: Send, t: "WhatsApp share" },
              { i: IndianRupee, t: "Online payments" },
              { i: FileText, t: "GST-compliant PDF" },
              { i: Layers, t: "HSN/SAC summary" },
              { i: Eye, t: "Edit-history audit trail" },
              { i: ShieldCheck, t: "Bank-grade security" },
              { i: Star, t: "4.9/5 rating" },
            ].map((f, i) => {
              const Icon = f.i;
              return (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-4">
                  <Icon className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-700">{f.t}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-4 sm:px-6 py-12 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-8" style={heading}>
            FAQs for {data.profession}
          </h2>
          <div className="space-y-2">
            {faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 py-14 bg-blue-600 text-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3" style={heading}>
            Ready to bill better?
          </h2>
          <p className="text-blue-100 mb-6">Free forever. No credit card. Made for India.</p>
          <Link
            to="/auth"
            data-testid="prof-cta-bottom"
            className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold rounded-lg px-7 py-3.5 hover:bg-blue-50"
            style={heading}
          >
            Start Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Internal link cluster */}
      <section className="px-4 sm:px-6 py-10 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.18em] mb-4 text-center">Other professions</p>
          <div className="flex flex-wrap justify-center gap-2">
            {PROFESSIONS.filter(p => p.slug !== slug).slice(0, 12).map((p) => (
              <Link
                key={p.slug}
                to={`/invoice-format/${p.slug}`}
                className="text-xs sm:text-sm bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:text-blue-700 hover:border-blue-300 transition-colors"
              >
                Invoice format for {p.profession}
              </Link>
            ))}
            {(data.relatedSeo || []).map((slug2) => {
              const lp = SEO_LANDING_PAGES.find(l => l.slug === slug2);
              if (!lp) return null;
              return (
                <Link
                  key={slug2}
                  to={`/${slug2}`}
                  className="text-xs sm:text-sm bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  {lp.h1.split("—")[0].trim()}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 px-4 text-center text-xs text-slate-400">
        Powered by <Link to="/" className="text-blue-600 font-semibold">InvoiceSetu</Link>
      </footer>
    </div>
  );
}

function Faq({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-white rounded-xl border border-slate-100" data-testid={`prof-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3"
      >
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}
